import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	formatDate,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
		sortDDLByLabel,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { DateRange } from "@mui/icons-material";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { SalaryProfileReportDataGridWithColumnToggled } from "./SalaryProfileReportDataGridWithColumnToggled";
import { SalaryProfileReportHeader } from "./SalaryProfileReportHeader";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface ISalaryProfileReportSetupPage { }
const SALARY_PROFILE_REPORT_QUERY_KEY = "salary-report";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";
const SALARY_PROFILE_LIST_QUERY_KEY = "salary-profile-list";

const useSalaryProfileReport = (
	runGetListAPICall: boolean,
	clientId: string,
	employeeStatus: string | number,
	employeeId: string,
	startDate: string | null,
	endDate: string | null,
	dateRange: boolean,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			SALARY_PROFILE_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,

			employeeStatus,
			employeeId,
			startDate,
			endDate,
			dateRange,
			params,
		],
		queryFn: () =>
			ReportsService.getSalaryProfileReport(
				runGetListAPICall,
				clientId,

				employeeStatus,
				employeeId,
				startDate,
				endDate,
				dateRange,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});


export const useGetLookupFieldData = (lookupFieldId: string) =>
	useQuery({
		queryKey: [GET_QUERY_KEY.LOOKUP_FIELD_DATA_BY_ID, lookupFieldId],
		queryFn: (data) => {
			return LookupFieldService.getByIdd(lookupFieldId);
		},
		refetchOnWindowFocus: false,
	});

const useGetMaskStatus = () =>
	useQuery({
		queryKey: ["mask-status"],
		queryFn: (data) => {
			return AuthService.getMaskStatus();
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDLItems = (
	clientId: string,
	status: number,
	taxYear: number
) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId, status, taxYear],
		queryFn: (data) => {
			return CompanyTD1FormService.getEmployeeDDLByStatus(clientId, status, taxYear);
		},
		refetchOnWindowFocus: false,
	});


export const SalaryProfileReport: React.FC<ISalaryProfileReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [salaryProfileReportData, setSalaryProfileReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);



	const {
		isLoading: isLoadingPayFrequency,
		data: employeeReportSetupPayFrequencyList,
	} = useGetLookupFieldData("payPeriods");

	const { isPending: isPendingMaskStatus, data: maskStatus } =
		useGetMaskStatus();

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const employeeId = formMethods.getValues("employee");
	const status = formMethods.getValues("employeeStatus");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");
	const dateRange = formMethods.getValues("dateRange");

	const { isPending, data } = useSalaryProfileReport(
		runGetListAPICall,
		clientIdStore,

		status && status,
		employeeId && employeeId,
		formatDate(startDate),
		formatDate(endDate),
		dateRange && dateRange,
		queryParams,
		navigateToLogout
	);
	const salaryProfileList: any | null = data;
	useEffect(() => {
		if (salaryProfileList) {
			setSalaryProfileReportData(salaryProfileList);
		}
	}, [salaryProfileList]);

	const { isPending: isPendingEmployeeDDLItems, data: employeeDDLItems } =
		useGetEmployeeDDLItems(clientId, employeeStatus, taxYear);

	//   after click apply button api calls with updated values and update salary report data
	const salaryProfileReportDataAPICall = (
		employeeStatus: string | number,
		employeeId: string,
		startDate: string,
		endDate: string,
		dateRange: boolean,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			// setQueryParams({
			// 	search_query: "",
			// 	order_by: "name",
			// 	order_direction: "asc",
			// 	page: 1,
			// 	size: 25,
			// });
			try {
				const response: any = await ReportsService.getSalaryProfileReport(
					true,
					clientIdStore,

					employeeStatus,
					employeeId,
					formatDate(startDate),
					formatDate(endDate),
					dateRange,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setSalaryProfileReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
	
		salaryProfileReportDataAPICall(
			values.employeeStatus,
			values.employee,
			values.startDate,
			values.endDate,
			values.dateRange,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getSalaryProfileAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [SALARY_PROFILE_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});

	
	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	// const { isPending: isSubmittingColumnReorder, mutate: submitColumnReorder } =
	// 	useMutation({
	// 		mutationFn: EmployeeService.submitColumnReorder,
	// 		onSuccess: (res: any | IServerError | IResponse) => {

	// 			if (res.status === 201) {
	// 				return queryClient.invalidateQueries({
	// 					queryKey: [SALARY_PROFILE_REPORT_QUERY_KEY],
	// 				});
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: res.message
	// 						? res.message
	// 						: "Something went wrong, try again later!",
	// 					severity: "error",
	// 				});
	// 			}
	// 		},
	// 		onError: (err: any) => {
	// 			setSnackbarData({
	// 				open: true,
	// 				message: err.message,
	// 				severity: "error",
	// 			});
	// 		},
	// 	});

	// useEffect(() => {
	// 	if (onColumnReorder) {
	// 		// submitColumnReorder({
	// 		// 	headerColumns: onColumnReorder,
	// 		// 	navigateToLogout: navigateToLogout,
	// 		// });
	// 	}
	// }, [onColumnReorder]);

	const salaryProfileReportDDlItems = useMemo(() => {
		if (employeeDDLItems) {
			if (
				employeeDDLItems &&
				Array.isArray(employeeDDLItems)
			) {
				let resources: IDropDownListResponse[] =
						sortDDLByLabel(employeeDDLItems).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDLItems]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedSalaryProfileListItems = useMemo(() => {
		return (
			salaryProfileReportData &&
			salaryProfileReportData.items &&
			salaryProfileReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [salaryProfileReportData]);

	const onEmployeeStatusSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		formMethods.setValue('employee', "all")
		setEmployeeStatus(event.value);
		// setSelectedEmployee(null);
		// formInitialState();
		formMethods.setValue('employeeStatus', event.value)
	};


	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"employee",
		"employeeStatus",
		"startDate",
		"endDate",
		"dateRange",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [employee, employeeStatus, startDate, endDate, dateRange] = values;

		try {
			setIsLoading(true);
			const response = await ReportsService.exportSalaryProfileReport(
				selectedOption,
				clientIdStore,
				employeeStatus,
				employee,
				formatDate(startDate),
				formatDate(endDate),
				dateRange,
				queryParams
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - Salary-Profile-Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - Salary-Profile-Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - Salary-Profile-Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};

	// ERD Data ItemsshowInR

	//  const showERDDataItems = (
	//   items: any[] = [],
	//   headerColumns: any[] = []
	// ) => {
	//   if (!items.length || !headerColumns.length) return [];

	//   return items.map(item => {
	//     const row: any = { ...item };

	
	//     headerColumns.forEach(col => {
	//       const field = col.field;

	//       if (col.edr === "Earning") {
	//         row[field] = item.erdEarnings?.[field] ?? 0;
	//       }

	//       if (col.edr === "Deduction") {
	//         row[field] = item.erdDeductions?.[field] ?? 0;
	//       }

	//       if (col.edr === "Reimbursement") {
	//         row[field] = item.erdReimbursements?.[field] ?? 0;
	//       }
	//     });
	//     return row;   
	//   });
	// };

	// const upadtedSalaryProfileListItemsSHow = useMemo(() => {
	//   return showERDDataItems(
	//     salaryProfileReportData?.items,
	//     salaryProfileReportData?.headerColumns
	//   );
	// }, [salaryProfileReportData]);


	const expandedLabels = ["Reports", "Employees"];
	const expandedSalaryProfileListOrSetupItem = "Salary";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Employees" &&
				item.label === expandedSalaryProfileListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedSalaryProfileListOrSetupItem && "/reports/salary",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			{/* <h3>Salary Profile Report</h3> */}
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Salary Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<SalaryProfileReportHeader
						salaryProfileReportDDlItems={salaryProfileReportDDlItems}
						// onEmployeeStatusSelectionChangeHandler={(e) =>
						// 	setEmployeeStatus(e.value)
						// }
						onEmployeeStatusSelectionChangeHandler={onEmployeeStatusSelectionChangeHandler}
						selectedItem={employeeStatus}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					// onApplyButtonClickedHandler={onApplyButtonClickedHandler}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading ||
						isPendingEmployeeDDLItems
					}
					testId="salary-report"
				/>

				<SalaryProfileReportDataGridWithColumnToggled
					columns={
						salaryProfileReportData?.headerColumns
							? salaryProfileReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// salaryProfileReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						salaryProfileReportData
							&& salaryProfileReportData
								?.totalHeaderColumns &&
							Object.keys(salaryProfileReportData.totalHeaderColumns).length !== 0 
							? salaryProfileReportData.totalHeaderColumns
							: {}
						// salaryProfileReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						salaryProfileReportData && updatedSalaryProfileListItems
							? updatedSalaryProfileListItems
							: []
						// salaryProfileReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						salaryProfileReportData && salaryProfileReportData.total
							? salaryProfileReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Salary Report"}
					onExportButtonClick={onExportButtonClick}
				/>

				{salaryProfileReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
